import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useData } from "../data/DataContext"
import { renameCategory } from "../data/api"
import { isReportablePerson } from "../data/types"
import { reportingYearForDate } from "../components/ObjectiveCard"

interface CategoryStats {
  name: string
  total: number
  active: number
  draft: number
  peopleWithCount: number
  gapCount: number
}

export function CategoriesPage() {
  const { people, objectives, ragScores, categories: taxonomyCategories, me, refresh } = useData()

  const canEditLabels = me.role === "Admin" || me.role === "Exec"

  // Sort order: taxonomy insertion order first, then anything in use that
  // isn't in the taxonomy list (alphabetical fallback).
  const categoryIndex = useMemo(() => {
    const m = new Map<string, number>()
    taxonomyCategories.forEach((c, i) => m.set(c.name, i))
    return m
  }, [taxonomyCategories])

  const years = useMemo(() => {
    const s = new Set<number>()
    for (const o of objectives) {
      if (o.status !== "Deleted" && o.year != null) s.add(o.year)
    }
    return [...s].sort((a, b) => b - a)
  }, [objectives])

  // Default to the current reporting year rather than `years[0]` (which is
  // the highest year present in data). Previously a single fat-fingered
  // objective with year=2037 would win the default and Chris would land on
  // a near-empty page. Current reporting year still falls back to years[0]
  // if no objective exists for it yet (e.g. brand-new instance).
  const currentReportingYear = reportingYearForDate()
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const defaultYear = years.includes(currentReportingYear)
    ? currentReportingYear
    : years[0] ?? null
  const effectiveYear = selectedYear ?? defaultYear

  // Exclude Team="User" from coverage counts — they’re self-service only,
  // not part of the board-facing Exec/CLT reporting surface.
  const activePeople = useMemo(() => people.filter(isReportablePerson), [people])
  const activePeopleNames = useMemo(
    () => new Set(activePeople.map((p) => p.fullName)),
    [activePeople],
  )

  const categories = useMemo(() => {
    const s = new Set<string>()
    for (const o of objectives) if (o.category) s.add(o.category)
    // Also surface any taxonomy categories that aren't yet in use, so Chris
    // can see them listed (with zero coverage) after adding them.
    for (const c of taxonomyCategories) s.add(c.name)
    return [...s].sort((a, b) => {
      const ai = categoryIndex.get(a) ?? 99
      const bi = categoryIndex.get(b) ?? 99
      if (ai !== bi) return ai - bi
      return a.localeCompare(b)
    })
  }, [objectives, taxonomyCategories, categoryIndex])

  const stats = useMemo<CategoryStats[]>(() => {
    return categories.map((cat) => {
      const inScope = objectives.filter(
        (o) =>
          o.category === cat &&
          o.status !== "Deleted" &&
          (effectiveYear == null || o.year === effectiveYear),
      )
      const active = inScope.filter((o) => o.status === "Active").length
      const draft = inScope.filter((o) => o.status === "Draft").length
      const peopleWith = new Set(
        inScope.map((o) => o.personName).filter((n) => activePeopleNames.has(n)),
      )
      return {
        name: cat,
        total: inScope.length,
        active,
        draft,
        peopleWithCount: peopleWith.size,
        gapCount: activePeople.length - peopleWith.size,
      }
    })
  }, [categories, objectives, effectiveYear, activePeopleNames, activePeople])

  const [editing, setEditing] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSaveRename = async (oldName: string, newName: string) => {
    setError(null)
    const trimmed = newName.trim()
    if (!trimmed || trimmed === oldName) {
      setEditing(null)
      return
    }
    const affectedObjectives = objectives.filter(
      (o) => o.category === oldName && o.status !== "Deleted",
    ).length
    const affectedRagScores = ragScores.filter(
      (r) => r.category === oldName,
    ).length
    const parts = [
      `${affectedObjectives} objective${affectedObjectives === 1 ? "" : "s"}`,
    ]
    if (affectedRagScores > 0) {
      parts.push(
        `${affectedRagScores} RAG score${affectedRagScores === 1 ? "" : "s"}`,
      )
    }
    const ok = window.confirm(
      `Rename "${oldName}" to "${trimmed}" on ${parts.join(" and ")}?\n\nThis cannot be undone from the app. All SharePoint rows will be updated.`,
    )
    if (!ok) return
    setBusy(true)
    try {
      await renameCategory(oldName, trimmed, objectives, ragScores)
      await refresh()
      setEditing(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      {/* Intro */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <p className="max-w-2xl text-[14px] leading-[1.6] text-ink-2">
          Coverage across{" "}
          <span className="font-medium text-ink">{activePeople.length} active people</span>
          {effectiveYear != null && <> · {effectiveYear}</>}.
          {canEditLabels ? (
            <>
              {" "}
              Click the pencil on any category to rename it across all objectives.
            </>
          ) : null}
        </p>
        {years.length > 0 ? (
          <label className="flex flex-col gap-[6px]">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
              Year
            </span>
            <select
              value={effectiveYear ?? ""}
              onChange={(e) =>
                setSelectedYear(e.target.value ? Number(e.target.value) : null)
              }
              className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {error ? (
        <div className="mb-4 rounded-md border border-rag-red bg-rag-red-bg p-3 text-[12.5px] text-rag-red">
          <p className="font-medium">Rename failed</p>
          <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">{error}</p>
        </div>
      ) : null}

      {/* Grid */}
      <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <CategoryTile
            key={s.name}
            stats={s}
            activeTotal={activePeople.length}
            editable={canEditLabels}
            isEditing={editing === s.name}
            busy={busy && editing === s.name}
            onStartEdit={() => setEditing(s.name)}
            onCancelEdit={() => {
              setEditing(null)
              setError(null)
            }}
            onSaveEdit={(newName) => handleSaveRename(s.name, newName)}
          />
        ))}
        {stats.length === 0 ? (
          <div className="col-span-full rounded-lg border border-dashed border-rule-strong bg-card p-8 text-center font-mono text-[11px] uppercase tracking-[0.12em] text-ink-3">
            No categories found in data
          </div>
        ) : null}
      </div>
    </div>
  )
}

/* ---------- Tile ---------- */

function CategoryTile({
  stats: s,
  activeTotal,
  editable,
  isEditing,
  busy,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
}: {
  stats: CategoryStats
  activeTotal: number
  editable: boolean
  isEditing: boolean
  busy: boolean
  onStartEdit: () => void
  onCancelEdit: () => void
  onSaveEdit: (newName: string) => void
}) {
  const coveragePct =
    activeTotal > 0 ? Math.round((s.peopleWithCount / activeTotal) * 100) : 0
  const barColor =
    coveragePct >= 90
      ? "bg-rag-green"
      : coveragePct >= 50
        ? "bg-rag-amber"
        : "bg-rag-red"

  const [draft, setDraft] = useState(s.name)

  // Reset draft when entering edit mode
  if (isEditing && draft !== s.name && !busy) {
    // noop — we want the input to be controlled by draft state after mount
  }

  const body = (
    <>
      {/* Coverage */}
      <div className="mt-[14px]">
        <div className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3">
          <span>Coverage · {coveragePct}%</span>
          <span>
            <span className="font-sans text-[14px] font-semibold normal-case tracking-normal text-ink">
              {s.peopleWithCount}
            </span>{" "}
            / {activeTotal}
          </span>
        </div>
        <div className="mt-[6px] h-[6px] w-full overflow-hidden rounded-full bg-paper-sunk">
          <div
            className={`h-full ${barColor} transition-all`}
            style={{ width: `${coveragePct}%` }}
          />
        </div>
      </div>

      {/* Counts */}
      <div className="mt-[14px] flex items-center gap-[14px] font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-3">
        <span>
          <span className="font-sans text-[14px] font-semibold normal-case tracking-normal text-ink">
            {s.total}
          </span>{" "}
          total
        </span>
        {s.active > 0 ? (
          <span className="text-rag-green">{s.active} active</span>
        ) : null}
        {s.draft > 0 ? (
          <span className="text-rag-amber-ink">{s.draft} draft</span>
        ) : null}
      </div>
    </>
  )

  /* Edit mode */
  if (isEditing) {
    return (
      <div className="rounded-lg border border-brass bg-card p-[18px] shadow-card">
        <div className="flex items-start gap-2">
          <input
            type="text"
            value={draft}
            autoFocus
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !busy) onSaveEdit(draft)
              if (e.key === "Escape" && !busy) {
                setDraft(s.name)
                onCancelEdit()
              }
            }}
            className="min-w-0 flex-1 rounded-sm border border-rule-strong bg-card px-[10px] py-[6px] font-display text-[16px] font-medium text-ink focus:border-nymr-navy focus:outline-none"
          />
        </div>
        <div className="mt-[10px] flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setDraft(s.name)
              onCancelEdit()
            }}
            className="rounded-sm border border-rule-strong bg-card px-[12px] py-[6px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-2 transition hover:border-ink-3 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !draft.trim() || draft.trim() === s.name}
            onClick={() => onSaveEdit(draft)}
            className="rounded-sm bg-nymr-navy px-[12px] py-[6px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-white transition hover:bg-nymr-navy-dark disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
        {body}
      </div>
    )
  }

  /* Normal mode */
  return (
    <div className="group relative rounded-lg border border-rule bg-card p-[18px] shadow-card transition hover:border-rule-strong">
      {/* Edit pencil (Admin/Exec only) */}
      {editable ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            setDraft(s.name)
            onStartEdit()
          }}
          title={`Rename "${s.name}"`}
          className="absolute right-[14px] top-[14px] grid h-7 w-7 place-items-center rounded-sm text-ink-3 opacity-0 transition hover:bg-paper-sunk hover:text-brass group-hover:opacity-100"
        >
          <svg viewBox="0 0 20 20" fill="none" className="h-[14px] w-[14px]">
            <path
              d="m4 16 1-4L13 4l3 3-8 8-4 1Zm9-12 3 3"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}

      <Link to={`/category/${encodeURIComponent(s.name)}`} className="block">
        <div className="flex items-start justify-between gap-2 pr-8">
          <h3 className="font-display text-[17px] font-medium leading-[1.3] tracking-[-0.005em] text-ink">
            {s.name}
          </h3>
        </div>
        <div className="mt-2">
          {s.gapCount > 0 ? (
            <span className="inline-flex items-center rounded-full bg-rag-amber-bg px-[9px] py-[2px] text-[11px] font-medium text-rag-amber-ink">
              {s.gapCount} gap{s.gapCount === 1 ? "" : "s"}
            </span>
          ) : s.total > 0 ? (
            <span className="inline-flex items-center rounded-full bg-rag-green-bg px-[9px] py-[2px] text-[11px] font-medium text-rag-green">
              Complete
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-rag-unset-bg px-[9px] py-[2px] text-[11px] font-medium text-ink-3">
              No data
            </span>
          )}
        </div>
        {body}
      </Link>
    </div>
  )
}
